import { Accordion, Col, dateUtils, Form } from '@valid-eval/shared-react-components';
import { debounce } from 'lodash';
import { useCallback, useEffect, useImperativeHandle, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Artifact } from 'data/features/artifactsTypes';
import { Event } from 'data/features/eventsTypes';
import { useUpdatePhase } from 'data/features/phasesHooks';
import { Phase } from 'data/features/phaseTypes';
import { useShowErrorIfRejected } from 'utils/hooks/useShowErrorIfRejected';
// import useBooleanFlag from 'utils/hooks/useBooleanFlag';
// import { useDeletePhase, useUpdatePhase } from 'data/features/phasesHooks';

import { phaseProgress } from '../hooks/useEditEventStepsState';
import { PhaseForm } from './PhaseForm';
import Styles from './PhasesItem.module.scss';
import { PhaseFormData } from './types';
import { transformPhaseFormValues } from './utils';
// import GenericModal from 'components/GenericModal';

const { dateToFormValue, isoDateToFormValue } = dateUtils;

export type PhasesItemRef = {
  save: () => Promise<void>;
};

type PhasesItemProps = {
  phase: Phase;
  event: Event;
  ref?: React.Ref<PhasesItemRef>;
};

export const artifactDefaultValues = (artifacts: Artifact[], selected: number[]) =>
  artifacts?.map((artifact) => (selected.includes(artifact.id) ? String(artifact.id) : false)) ||
  [];

function PhasesItem({ phase, event, ref }: PhasesItemProps) {
  const { t } = useTranslation();
  const showErrorIfRejected = useShowErrorIfRejected();
  const [activeKey, setActiveKey] = useState<string | null>(null);
  // const [showDeleteModal, openDeleteModal, closeDeleteModal] = useBooleanFlag(false);
  // const { deletePhase, deleting } = useDeletePhase();
  const { updatePhase } = useUpdatePhase();
  const progress = phaseProgress(phase);

  const methods = useForm<PhaseFormData>({
    mode: 'all',
    defaultValues: {
      id: phase.id,
      name: phase.name,
      teams_anticipated_next_round: phase.teams_anticipated_next_round,
      start_date: isoDateToFormValue(phase.start_date),
      end_date: isoDateToFormValue(phase.end_date),
      consensus_meeting_date: dateToFormValue(phase.consensus_meeting_date),
      feedback_available_date: dateToFormValue(phase.feedback_available_date),
      artifact_ids_checkboxes: artifactDefaultValues(event.artifacts, phase.artifact_ids || []),
    },
  });

  // const handleConfirmDelete = async () => {
  //   const [, hasError] = showErrorIfRejected(await deletePhase(event.id, phase.id));
  //   if (!hasError) {
  //     success(t('event.form.delete_phase_success'));
  //     closeDeleteModal();
  //   }
  // };

  const save = async (data: PhaseFormData) => {
    const [,] = showErrorIfRejected(await updatePhase(phase.id, transformPhaseFormValues(data)));
  };

  // expose save method to parent component
  useImperativeHandle(
    ref,
    () => ({
      // show phase accordion if save validation fails
      save: () => {
        return new Promise((resolve, reject) => {
          methods.handleSubmit(
            (data) => {
              save(data);
              resolve();
            },
            () => {
              setActiveKey('0');
              reject();
            },
          )();
        });
      },
    }),
    [methods, save],
  );

  const debouncedSave = useCallback(
    debounce((data: PhaseFormData) => {
      const errorKeys = Object.keys(methods.formState.errors);
      for (const errorKey of errorKeys) {
        delete data[errorKey as keyof PhaseFormData];
      }
      save(data);
    }, 1000),
    [],
  );

  // Submit form when values change
  useEffect(() => {
    methods.trigger();
    const subscription = methods.watch((_, { type }) => {
      if (type !== 'change') return;
      debouncedSave(methods.getValues());
    });
    return () => {
      subscription.unsubscribe();
      debouncedSave.cancel();
    };
  }, [methods.watch, debouncedSave]);

  // const handleDeleteClick = (e: React.MouseEvent) => {
  //   e.preventDefault();
  //   e.stopPropagation();
  //   openDeleteModal();
  // };

  return (
    <Col xs={12} className="mb-3">
      <Accordion
        activeKey={activeKey}
        onSelect={(eventKey) => setActiveKey(eventKey as string | null)}
      >
        <Accordion.Item eventKey="0" className={Styles.AccordionItem}>
          <Accordion.Header className={Styles.AccordionHeader} as="div">
            <div className="d-flex align-items-center w-100">
              <h3 className="m-0 p-0 flex-grow-1">
                {phase.name}&nbsp;&nbsp;
                <small className="text-muted">
                  {t('event.form.progress', {
                    completed: progress.completed,
                    total: progress.total,
                  })}
                </small>
              </h3>
              {/* <Button
                variant="link"
                as="div"
                className="px-2 py-2 me-4"
                onClick={handleDeleteClick}
              >
                <i className="fa-solid fa-trash-can m-0"></i>
              </Button> */}
              <i className="fa-solid fa-angle-up fa-lg m-0"></i>
            </div>
          </Accordion.Header>
          <Accordion.Body>
            <FormProvider {...methods}>
              <Form onSubmit={methods.handleSubmit(save)}>
                <PhaseForm event={event} phase={phase} />
              </Form>
            </FormProvider>
            {/* <GenericModal
              show={showDeleteModal}
              onCancel={closeDeleteModal}
              title={t('event.form.delete_phase_title')}
              body={t('event.form.delete_phase_confirm', { name: phase.name })}
              confirmButton={t('common.confirm')}
              cancelButton={t('common.cancel')}
              onConfirm={handleConfirmDelete}
              isButtonDisabled={deleting}
              isCancelButtonDisabled={deleting}
            /> */}
          </Accordion.Body>
        </Accordion.Item>
      </Accordion>
    </Col>
  );
}

export default PhasesItem;
